import React, { useState, useEffect } from 'react';
import { TrendingUp, Calendar, ShieldCheck, Activity, Timer, Zap, Layers, ArrowUpRight, Sparkles } from 'lucide-react';
import apiService from '../services/api';
import { useTranslation } from 'react-i18next';
import { useTradingContext } from '../context/TradingContext';
import { useAuth } from '../hooks/useAuth';

export default function MyInvestments() {
  const { t } = useTranslation();
  const { marketPrices } = useTradingContext();
  const { isAuthenticated } = useAuth();
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchInvestments();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchInvestments = async () => {
    try {
      const response = await apiService.get('/investments/my-investments');
      if (response.success) {
        setInvestments(response.data);
      }
    } catch (error) {
      console.error("Error fetching user investments:", error);
    } finally {
      setLoading(false);
    }
  };

  const getLiveStats = (inv) => {
    const start = new Date(inv.start_date);
    const end = new Date(inv.end_date);
    const totalDuration = end - start;
    const elapsed = now - start;
    const remaining = end - now;
    const isCompleted = remaining <= 0 || inv.status === 'completed';
    
    const progressPercent = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
    
    const dailyRate = parseFloat(inv.daily_return_percent) / 100;
    const hourlyRate = dailyRate / 24;
    const secondRate = hourlyRate / 3600;
    
    const lastPayout = inv.last_payout_date ? new Date(inv.last_payout_date) : start;
    const payoutCursor = isCompleted ? end : now;
    const msSinceLastPayout = payoutCursor - lastPayout;
    const secondsSinceLastPayout = Math.max(0, msSinceLastPayout / 1000);
    
    const currentLiveProfit = isCompleted ? 0 : (parseFloat(inv.amount) * secondRate * secondsSinceLastPayout);
    const totalLiveProfit = parseFloat(inv.total_profit || 0) + currentLiveProfit;

    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const mins = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const secs = Math.floor((remaining % (1000 * 60)) / 1000);

    return {
      progressPercent,
      totalLiveProfit,
      projectedWalletCredit: parseFloat(inv.amount || 0) + totalLiveProfit,
      timeRemaining: remaining > 0 ? `${days}j ${hours}h ${mins}m ${secs}s` : 'Terminé',
      isCompleted,
      days,
      hours,
      mins,
      secs
    };
  };

  const getAmountInUSDT = (amount, assetSymbol) => {
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) return 0;
    if (assetSymbol === 'USDT') return numericAmount;
    const symbol = `${assetSymbol}USDT`;
    const marketData = marketPrices[symbol];
    if (marketData && marketData.price) {
      return numericAmount * marketData.price;
    }
    return numericAmount;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-6">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-t-2 border-emerald-500 animate-spin" style={{ boxShadow: '0 0 30px rgba(16,185,129,0.3)' }}></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity size={24} className="text-emerald-400 animate-pulse" />
          </div>
        </div>
        <p className="text-slate-500 font-black uppercase tracking-[0.4em] text-[10px]">{t('common.analyzing')}</p>
      </div>
    );
  }

  if (investments.length === 0) return null;

  const totalInvestedUSDT = investments.reduce((acc, inv) => acc + getAmountInUSDT(inv.amount, inv.asset_symbol), 0);
  
  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 mb-20 md:mb-28">
      {/* Portfolio Header */}
      <div className="mb-10 md:mb-14 relative p-6 md:p-10 rounded-[24px] md:rounded-[32px] overflow-hidden" 
           style={{
             background: 'linear-gradient(135deg, rgba(10,15,30,0.9) 0%, rgba(15,22,40,0.95) 50%, rgba(8,12,25,0.9) 100%)',
             border: '1px solid rgba(99,102,241,0.15)',
             boxShadow: '0 0 60px rgba(99,102,241,0.08), inset 0 1px 0 rgba(255,255,255,0.05)'
           }}>
        <div className="absolute top-0 right-0 w-60 h-60 bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-500/8 rounded-full blur-[80px] pointer-events-none"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4"
                 style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)' }}>
              <Zap size={12} className="text-indigo-400 animate-pulse" />
              <span className="text-[9px] font-black text-indigo-400 uppercase tracking-[0.25em]">{t('investments.nova_core_engine')}</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-black font-outfit uppercase tracking-tight text-white leading-none">
              {t('investments.performance')} <span className="bg-gradient-to-r from-indigo-400 to-emerald-400 bg-clip-text text-transparent">{t('investments.real_time')}</span>
            </h2>
          </div>
          
          <div className="flex gap-4 md:gap-8">
            <div className="px-5 py-3 rounded-2xl" style={{ background: 'rgba(10,15,30,0.8)', border: '1px solid rgba(99,102,241,0.15)' }}>
              <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1">{t('investments.allocated_value')}</p>
              <p className="text-xl md:text-2xl font-black text-white font-outfit tabular-nums">
                {totalInvestedUSDT.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-[9px] text-slate-600">USDT</span>
              </p>
            </div>
            <div className="px-5 py-3 rounded-2xl" style={{ background: 'rgba(10,30,20,0.8)', border: '1px solid rgba(16,185,129,0.15)' }}>
              <p className="text-[8px] font-black text-emerald-500/60 uppercase tracking-widest mb-1">{t('investments.consolidated_gains')}</p>
              <p className="text-xl md:text-2xl font-black text-emerald-400 font-outfit tabular-nums">
                +{investments.reduce((acc, inv) => acc + getAmountInUSDT(getLiveStats(inv).totalLiveProfit, inv.asset_symbol), 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })} <span className="text-[9px] text-emerald-900">USDT</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Investment Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        {investments.map((inv) => {
          const stats = getLiveStats(inv);
          
          return (
            <div key={inv.id} className="group relative">
              {/* Glow effect */}
              <div className="absolute -inset-1 rounded-[28px] opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                   style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(16,185,129,0.1))', filter: 'blur(20px)' }}></div>
              
              {/* Main Card */}
              <div className="relative rounded-[24px] md:rounded-[28px] overflow-hidden transition-all duration-500 group-hover:-translate-y-1"
                   style={{
                     background: 'linear-gradient(180deg, rgba(12,18,35,0.95) 0%, rgba(8,12,25,0.98) 100%)',
                     border: '1px solid rgba(255,255,255,0.06)',
                     boxShadow: '0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)'
                   }}>
                
                {/* Top Header Bar */}
                <div className="px-5 md:px-7 pt-5 md:pt-6 pb-4 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 ${
                      stats.isCompleted 
                        ? 'text-emerald-400' 
                        : 'text-indigo-400'
                    }`}
                    style={{ 
                      background: stats.isCompleted ? 'rgba(16,185,129,0.1)' : 'rgba(99,102,241,0.1)',
                      border: `1px solid ${stats.isCompleted ? 'rgba(16,185,129,0.2)' : 'rgba(99,102,241,0.2)'}` 
                    }}>
                      <div className={`w-1.5 h-1.5 rounded-full ${stats.isCompleted ? 'bg-emerald-500' : 'bg-indigo-500 animate-pulse'}`}></div>
                      {stats.isCompleted ? t('investments.completed') : t('investments.in_progress')}
                    </div>
                    <span className="text-[9px] font-bold text-slate-600 uppercase tracking-widest">Hash: #{inv.id.toString().slice(-6)}</span>
                  </div>
                  <div className="px-2.5 py-1 rounded-lg text-[9px] font-black text-cyan-400 uppercase tracking-widest"
                       style={{ background: 'rgba(6,182,212,0.08)', border: '1px solid rgba(6,182,212,0.15)' }}>
                    {t('investments.daily_yield')}
                  </div>
                </div>

                {/* Capital & Profit Row */}
                <div className="px-5 md:px-7 grid grid-cols-2 gap-3 mb-4">
                  <div className="p-4 rounded-2xl relative overflow-hidden"
                       style={{ background: 'rgba(10,15,30,0.8)', border: '1px solid rgba(99,102,241,0.08)' }}>
                    <span className="text-[8px] font-black text-slate-500 uppercase tracking-[0.2em] block mb-1">{t('investments.invested_capital')}</span>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xl md:text-2xl font-black text-white font-outfit">{parseFloat(inv.amount).toLocaleString()}</span>
                      <span className="text-[10px] font-bold text-slate-500 uppercase">{inv.asset_symbol}</span>
                    </div>
                  </div>
                  <div className="p-4 rounded-2xl relative overflow-hidden"
                       style={{ background: 'rgba(10,30,20,0.6)', border: '1px solid rgba(16,185,129,0.1)' }}>
                    <span className="text-[8px] font-black text-emerald-500/60 uppercase tracking-[0.2em] block mb-1">{t('investments.total_profit')}</span>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xl md:text-2xl font-black text-emerald-400 font-outfit">+{stats.totalLiveProfit.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span>
                      <span className="text-[10px] font-bold text-emerald-900 uppercase">{inv.asset_symbol}</span>
                    </div>
                  </div>
                </div>

                {/* Central 3D Coin Section */}
                <div className="mx-5 md:mx-7 mb-4 p-6 md:p-8 rounded-[20px] relative overflow-hidden flex flex-col items-center"
                     style={{
                       background: 'linear-gradient(180deg, rgba(8,12,25,0.9) 0%, rgba(10,18,35,0.95) 50%, rgba(8,12,25,0.9) 100%)',
                       border: '1px solid rgba(99,102,241,0.08)',
                       boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03), inset 0 -1px 0 rgba(0,0,0,0.2)'
                     }}>
                  
                  {/* Decorative glow behind coin */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full pointer-events-none"
                       style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)' }}></div>
                  
                  <span className="text-[9px] font-black text-slate-500 uppercase tracking-[0.4em] mb-3 flex items-center gap-2 relative z-10">
                    <Zap size={10} className="text-yellow-500 fill-yellow-500" />
                    {t('investments.nova_session_gains')}
                  </span>
                  
                  <div className="flex items-baseline gap-2 mb-5 relative z-10">
                    <span className="text-2xl md:text-4xl font-black text-emerald-400 font-outfit tracking-tight tabular-nums"
                          style={{ textShadow: '0 0 30px rgba(52,211,153,0.3)' }}>
                      +{stats.totalLiveProfit.toLocaleString(undefined, { minimumFractionDigits: 8, maximumFractionDigits: 8 })}
                    </span>
                    <span className="text-sm font-black text-emerald-900 uppercase">{inv.asset_symbol}</span>
                  </div>

                  {/* 3D Coin Element */}
                  <div className="relative w-20 h-20 mb-4">
                    {/* Glow ring */}
                    <div className="absolute inset-[-8px] rounded-full animate-[spin_8s_linear_infinite]"
                         style={{ background: 'conic-gradient(from 0deg, transparent, rgba(99,102,241,0.3), transparent, rgba(16,185,129,0.3), transparent)' }}></div>
                    {/* Platform disc */}
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full"
                         style={{ background: 'linear-gradient(180deg, rgba(99,102,241,0.15) 0%, rgba(99,102,241,0.02) 100%)', filter: 'blur(4px)' }}></div>
                    {/* Coin body */}
                    <div className="relative w-20 h-20 rounded-full flex items-center justify-center animate-[float_3s_ease-in-out_infinite]"
                         style={{
                           background: 'linear-gradient(135deg, #1a2550 0%, #0f1a35 30%, #162040 60%, #0d1530 100%)',
                           border: '2px solid rgba(99,102,241,0.3)',
                           boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(99,102,241,0.15), inset 0 2px 4px rgba(255,255,255,0.05)'
                         }}>
                      <span className="text-2xl font-black font-outfit bg-gradient-to-b from-indigo-300 to-emerald-400 bg-clip-text text-transparent">N</span>
                      {/* Shine effect */}
                      <div className="absolute top-2 left-3 w-6 h-3 rounded-full opacity-30"
                           style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.4), transparent)' }}></div>
                    </div>
                  </div>

                  {/* Real-time badge */}
                  <div className="flex items-center gap-1.5 px-3 py-1 rounded-full relative z-10"
                       style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.15)' }}>
                    <Activity size={10} className="text-emerald-400 animate-pulse" />
                    <span className="text-[8px] font-black text-emerald-400 uppercase tracking-[0.15em]">{t('investments.nanosecond_update')}</span>
                  </div>
                </div>

                {/* Time & Progress */}
                <div className="px-5 md:px-7 mb-4">
                  <div className="flex justify-between items-end mb-3">
                    <div>
                      <div className="flex items-center gap-1.5 text-slate-500 mb-1">
                        <Timer size={11} />
                        <span className="text-[8px] font-black uppercase tracking-widest">{t('investments.time_remaining')}</span>
                      </div>
                      <p className="text-sm md:text-base font-black text-white font-outfit tabular-nums">{stats.timeRemaining}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-[8px] font-black text-indigo-400 uppercase tracking-widest block mb-1">{t('investments.cycle_progression')}</span>
                      <p className="text-lg font-black text-white font-outfit">{stats.progressPercent.toFixed(2)}%</p>
                    </div>
                  </div>
                  
                  {/* Neon Progress Bar */}
                  <div className="relative h-2 rounded-full overflow-hidden"
                       style={{ background: 'rgba(10,15,30,0.8)', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <div className="h-full rounded-full transition-all duration-1000 relative"
                         style={{
                           width: `${stats.progressPercent}%`,
                           background: 'linear-gradient(90deg, #6366f1 0%, #818cf8 40%, #34d399 100%)',
                           boxShadow: '0 0 12px rgba(99,102,241,0.5), 0 0 4px rgba(52,211,153,0.3)'
                         }}>
                      <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.15)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.15)_50%,rgba(255,255,255,0.15)_75%,transparent_75%,transparent)] bg-[length:1rem_1rem] animate-[progress_2s_linear_infinite]"></div>
                    </div>
                  </div>
                </div>

                {/* Bottom Info Grid */}
                <div className="px-5 md:px-7 grid grid-cols-2 gap-2 mb-4">
                  <div className="p-3 rounded-xl flex items-center gap-2.5"
                       style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <div className="p-1.5 rounded-lg" style={{ background: 'rgba(99,102,241,0.08)' }}>
                      <Calendar size={13} className="text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-[7px] font-black text-slate-600 uppercase tracking-widest">{t('investments.activation')}</p>
                      <p className="text-[9px] font-bold text-white">{new Date(inv.start_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="p-3 rounded-xl flex items-center gap-2.5"
                       style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <div className="p-1.5 rounded-lg" style={{ background: 'rgba(99,102,241,0.08)' }}>
                      <ShieldCheck size={13} className="text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-[7px] font-black text-slate-600 uppercase tracking-widest">{t('investments.protection')}</p>
                      <p className="text-[9px] font-bold text-emerald-400">NOVA SHIELD</p>
                    </div>
                  </div>
                </div>

                {/* Footer - Guaranteed Payout */}
                <div className="px-5 md:px-7 py-3.5 flex items-center justify-between"
                     style={{
                       background: 'linear-gradient(135deg, rgba(16,185,129,0.06) 0%, rgba(16,185,129,0.02) 100%)',
                       borderTop: '1px solid rgba(16,185,129,0.1)'
                     }}>
                  <div className="flex items-center gap-2">
                    <Layers size={13} className="text-emerald-400" />
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.15em]">
                      Payout en {inv.asset_symbol}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                       style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.12)' }}>
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                    <span className="text-[8px] font-black text-emerald-400 uppercase tracking-widest">100% Garanti</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}